import axios from "axios";
import NProgress from "nprogress";
import { backendPort } from "../components/Fungsiku";
import { getSessionToken } from "./authSession";

const api = axios.create({
  baseURL: backendPort,
  withCredentials: true,
});

const pendingControllers = new Map();
const pendingListeners = new Set();
const makeReqId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
const normalizePath = (url) => {
  const raw = String(url || "").trim();
  if (!raw) return "";
  const noQuery = raw.split("?")[0];
  if (noQuery.startsWith("http://") || noQuery.startsWith("https://")) {
    try {
      return new URL(noQuery).pathname || "";
    } catch {
      return noQuery;
    }
  }
  return noQuery;
};
const notifyPendingChange = () => {
  const count = pendingControllers.size;
  pendingListeners.forEach((listener) => {
    try {
      listener(count);
    } catch (err) {
      console.error("pending listener error:", err);
    }
  });
};

// interceptor request
api.interceptors.request.use(config => {
  NProgress.start();

  const reqId = makeReqId();
  const reqGroup = config.requestGroup || 'default';
  config.__reqId = reqId;
  config.__reqGroup = reqGroup;

  if (!config.signal) {
    const controller = new AbortController();
    config.signal = controller.signal;
    pendingControllers.set(reqId, {
      controller,
      group: reqGroup,
      startedAt: Date.now(),
      method: String(config?.method || "get").toLowerCase(),
      path: normalizePath(config?.url),
    });
    notifyPendingChange();
  }

  const token = getSessionToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

// interceptor response
api.interceptors.response.use(
  response => {
    NProgress.done();
    if (response?.config?.__reqId) {
      pendingControllers.delete(response.config.__reqId);
      notifyPendingChange();
    }
    return response;
  },
  error => {
    NProgress.done();
    if (error?.config?.__reqId) {
      pendingControllers.delete(error.config.__reqId);
      notifyPendingChange();
    }
    return Promise.reject(error);
  }
);

api.cancelPendingRequests = ({ group, reason } = {}) => {
  pendingControllers.forEach((item, reqId) => {
    if (group && item.group !== group) return;
    item.controller.abort(reason || 'Canceled by user refresh');
    pendingControllers.delete(reqId);
  });
  notifyPendingChange();
  NProgress.done();
};

api.isCanceledError = (error) => (
  axios.isCancel?.(error)
  || error?.code === 'ERR_CANCELED'
  || error?.name === 'CanceledError'
);

api.getPendingRequestCount = () => pendingControllers.size;
api.subscribePendingRequests = (listener) => {
  if (typeof listener !== "function") return () => {};
  pendingListeners.add(listener);
  listener(pendingControllers.size);
  return () => {
    pendingListeners.delete(listener);
  };
};

export default api;
